/** `adoption` namespace — English (structural parity enforced by the type). */
import type adoptionFr from './adoption.fr';

const adoptionEn: typeof adoptionFr = {
  plans: {
    eyebrow: 'Pricing',
    title: 'A plan for every stage of your growth',
    subtitle:
      'Start free, then scale at your own pace. Every plan includes AI credits, multi-network publishing and the Copilot.',
    currency: 'Currency',
    billingFrequency: 'Billing frequency',
    monthly: 'Monthly',
    annual: 'Annual',
    annualHint: '2 months free',
    popularBadge: 'Most popular',
    founderBadge: 'Best for solo founders',
    free: 'Free',
    perMonth: '/ month',
    billedMonthly: 'Billed monthly',
    billedAnnually: 'Billed annually — {{total}} / year',
    priceFallback: '≈ billed in {{currency}}',
    priceFallbackHint: 'Currency not configured for this plan — price shown in the default currency.',
    credits: '{{count}} AI credits per month',
    overage: 'Extra credits at {{price}} each',
    unlimited: 'unlimited',
    ctaChoose: 'Choose {{plan}}',
    ctaStartFree: 'Start for free',
    trialBadge: '{{days}}-day Pro trial, no credit card',
    fallbackNote: 'Live pricing is temporarily unavailable — here is an overview of our plans.',
    retry: 'Retry',
    limits: {
      contacts: 'Contacts',
      seats: 'Users',
      projects: 'Projects',
      sequences: 'Sequences',
      ai_agents: 'AI agents',
      external_api_rpm: 'Developer API (req/min)',
    },
    features: {
      ai_teams: 'AI team (specialist agents)',
      advanced_analytics: 'Advanced analytics & attribution',
      content_studio: 'AI content studio',
      automations: 'Automations',
      marketplace: 'Services marketplace',
      byok_llm: 'Bring your own LLM key (BYOK)',
    },
    descriptions: {
      free: 'To get started.',
      founder: 'For solo founders: the complete AI strategy loop.',
      starter: 'For freelancers and small teams.',
      pro: 'For firms and agencies.',
      enterprise: 'Custom volume and limits.',
    },
    teaser: {
      freeName: 'Free',
      freePrice: '$0',
      freeTagline: 'Try AI content creation and publishing on your first networks.',
      founderName: 'Founder',
      founderPrice: 'Solo',
      founderTagline: 'The complete AI strategy loop for a solo founder.',
      proName: 'Pro',
      proPrice: 'Team',
      proTagline: 'For teams and agencies that publish and prospect every day.',
      period: 'per month',
      featureCredits: 'AI credits included every month',
      featureNetworks: 'Multi-network publishing',
      featureCopilot: 'AI Copilot',
      featureAiTeam: 'AI team & advanced analytics',
      featureSeats: 'Team collaboration',
    },
  },
  preset: {
    sectionTitle: 'Workspace mode',
    intro:
      'Choose the navigation that fits how you work. Hidden modules stay reachable in the "More" group and by direct link.',
    founderLabel: 'Solo founder',
    founderDesc:
      'The essentials to launch and grow your product: strategy, content, publishing, analytics and Copilot. Everything else lives under "More".',
    teamLabel: 'Team',
    teamDesc: 'The full navigation, for a team using every module.',
    agencyLabel: 'Agency',
    agencyDesc: 'Full navigation to manage several brands and clients.',
    enterpriseLabel: 'Enterprise',
    enterpriseDesc: 'Full navigation with governance, roles and compliance.',
    current: 'Current',
    save: 'Apply this mode',
    saving: 'Saving…',
    saved: 'Workspace mode saved.',
    readOnly: 'Only owners and admins can change the mode.',
    loadError: 'Could not load the workspace mode.',
    saveError: 'Could not save the mode.',
    moreGroup: 'More',
    founderHub: 'Founder mode',
    groups: {
      home: 'Today & strategy',
      content: 'Content & publishing',
      analytics: 'Analytics',
      copilot: 'Copilot',
      settings: 'Settings',
    },
  },
  proof: {
    networks: 'social networks supported',
    resultsLink: 'See our customers’ results',
  },
  results: {
    eyebrow: 'Results',
    title: 'Results published with our customers’ written consent',
    subtitle:
      'Every case study below is written by the customer, published with their written consent and removed as soon as they withdraw it.',
    empty: 'No case study published yet.',
    emptyHint: 'Our first customers are preparing their stories. Check back soon!',
    loadError: 'Case studies could not be loaded right now.',
    retry: 'Retry',
    consentBadge: 'Published with written consent — {{date}}',
    challenge: 'The challenge',
    solution: 'The solution',
    results: 'The results',
    metricsFromAttribution: 'Measured with Karexia attribution',
    visitWebsite: 'Visit website',
    backHome: 'Back to home',
    language: 'Language',
    ctaTitle: 'Your story here?',
    ctaBody: 'Create your account and publish your own results from Settings → Case studies.',
    ctaButton: 'Create an account',
  },
  caseStudies: {
    title: 'Case studies',
    intro:
      'Share your results on the public "Results" page. Nothing is published without your written consent, and you can withdraw it at any time.',
    newButton: 'New case study',
    empty: 'No case study yet.',
    emptyHint: 'Write your first case study, sign the consent, then our team publishes it after review.',
    viewPublic: 'View public page',
    form: {
      createTitle: 'New case study',
      editTitle: 'Edit case study',
      companyName: 'Company name',
      industry: 'Industry',
      website: 'Website (https://…)',
      language: 'Publication language',
      title: 'Title',
      summary: 'Summary',
      challenge: 'The challenge',
      solution: 'The solution',
      results: 'The results',
      quote: 'Quote',
      quoteAuthor: 'Quote author',
      quoteAuthorTitle: 'Author’s role',
      metrics: 'Result metrics',
      metricLabel: 'Label',
      metricValue: 'Value',
      addMetric: 'Add a metric',
      removeMetric: 'Remove',
      importAttribution: 'Import from attribution (90 days)',
      importing: 'Importing…',
      importDone: 'Metrics imported from attribution.',
      importEmpty: 'No attributed conversion in the last 90 days.',
      importError: 'Attribution is unavailable on your plan or not configured.',
      metricsPermission: 'I allow these numeric metrics to be published',
      attributedConversions: 'Attributed conversions (90 d)',
      attributedRevenue: 'Attributed revenue (90 d)',
      topChannel: 'Top channel',
      save: 'Save draft',
      saving: 'Saving…',
      cancel: 'Cancel',
      saved: 'Case study saved.',
      saveError: 'Could not save.',
      editWarning: 'Editing a signed case study voids the consent: you will need to sign again.',
      required: 'Required field',
    },
    status: {
      draft: 'Draft',
      pending_review: 'In review',
      published: 'Published',
      rejected: 'Rejected',
      consent_revoked: 'Consent withdrawn',
    },
    consent: {
      title: 'Written consent to publish',
      intro: 'Read the text below. Your name, the date, your IP address and this text are kept as evidence.',
      checkbox: 'I have read and accept this consent',
      signedName: 'Signatory’s full name',
      signedTitle: 'Signatory’s role',
      sign: 'Sign and submit for publication',
      signing: 'Signing…',
      signed: 'Consent signed — your case study is in review.',
      signError: 'Could not sign.',
      versionOutdated: 'The consent text has changed. Read it again, then sign.',
      signedBy: 'Signed by {{name}} on {{date}}',
      stale: 'The content changed after signing: sign again to publish.',
      revoke: 'Withdraw my consent',
      revokeConfirm: 'Withdraw your consent? The case study will be removed from the public page immediately.',
      revokeReason: 'Reason (optional)',
      revoked: 'Consent withdrawn — the case study is no longer public.',
      revokeError: 'Could not withdraw.',
      history: 'Consent history',
      historyEmpty: 'No signed consent.',
      revokedOn: 'Withdrawn on {{date}}',
      active: 'Active',
    },
    actions: {
      edit: 'Edit',
      delete: 'Delete',
      deleteConfirm: 'Delete this case study? The consent evidence is kept.',
      deleted: 'Case study deleted.',
      signConsent: 'Sign the consent',
      showHistory: 'History',
      hideHistory: 'Hide history',
    },
    reviewNote: 'Review note: {{note}}',
    publicBadge: 'Visible on /results',
    readOnly: 'Read only — only owners and admins can edit or sign.',
    loadError: 'Could not load case studies.',
  },
  adminCaseStudies: {
    title: 'Customer case studies',
    intro:
      'Review case studies submitted by customers. Only those carrying an active written consent that matches the content can be published.',
    filterAll: 'All',
    empty: 'No case study in this queue.',
    approve: 'Publish',
    reject: 'Reject',
    unpublish: 'Unpublish',
    note: 'Review note',
    tenant: 'Organization',
    consentOk: 'Active consent — {{name}}, {{date}}',
    consentMissing: 'No active consent covers this content',
    approved: 'Case study published.',
    rejected: 'Case study rejected.',
    unpublished: 'Case study unpublished.',
    actionError: 'Action failed.',
  },
  guides: {
    linkLabel: 'Step-by-step guide',
    openInNewTab: '(opens the documentation in a new tab)',
  },
};

export default adoptionEn;
